/**
 * Utilities for parsing JSON Schema $ref pointers.
 */
/**
 * Decode a JSON pointer segment according to RFC 6901.
 * ~1 -> /
 * ~0 -> ~
 */
export function decodeJsonPointerSegment(segment) {
    return segment.replace(/~1/g, '/').replace(/~0/g, '~');
}
/**
 * Parse a JSON pointer $ref string into path segments.
 * e.g., "#/definitions/Person" -> ["definitions", "Person"]
 *
 * @param ref - The $ref string (e.g., "#/definitions/Person")
 * @returns Array of decoded path segments, or empty array if invalid
 */
export function parseRefPointer(ref) {
    if (!ref || !ref.startsWith('#/'))
        return [];
    return ref.slice(2).split('/').map(decodeJsonPointerSegment);
}
/**
 * Extract the shape/type name from a $ref string.
 * This is the last segment of the ref path.
 *
 * @example
 * parseShapeName("#/definitions/agentTransitionImmediate") // "agentTransitionImmediate"
 * parseShapeName("#/$defs/Person") // "Person"
 * parseShapeName(undefined) // undefined
 */
export function parseShapeName(ref) {
    if (!ref)
        return undefined;
    const segments = parseRefPointer(ref);
    return segments.length > 0 ? segments[segments.length - 1] : undefined;
}
/**
 * Check if a string is a valid $ref pointer.
 */
export function isRefPointer(ref) {
    return typeof ref === 'string' && ref.startsWith('#/');
}
/**
 * Get the form config for a shape by its $ref.
 *
 * @example
 * getShapeFormConfig(forms, "#/definitions/agentExecutionChat")
 * // { title: "Chat", order: [...], properties: {...} }
 */
export function getShapeFormConfig(forms, ref) {
    if (!forms || !ref)
        return undefined;
    const shapeName = parseShapeName(ref);
    if (!shapeName)
        return undefined;
    return forms[shapeName]?.default;
}
/**
 * Get the title for a shape from form config.
 * Falls back to undefined if no form config or title exists.
 *
 * @example
 * getShapeTitle(forms, "#/definitions/agentAiStateAttachmentsNone") // "None"
 */
export function getShapeTitle(forms, ref) {
    return getShapeFormConfig(forms, ref)?.title;
}
/**
 * Get the field config for a property within a shape.
 *
 * @example
 * getShapeFieldConfig(forms, "#/definitions/agentExecutionChat", "systemPrompt")
 * // { title: "System Prompt", widget: "paragraphText", minRows: 4 }
 */
export function getShapeFieldConfig(forms, ref, fieldName) {
    if (!fieldName)
        return undefined;
    return getShapeFormConfig(forms, ref)?.properties?.[fieldName];
}
